import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiCheckCircle, FiEye, FiEyeOff, FiLock } from "react-icons/fi";
import api, { apiError } from "../../lib/api";
import RenderToast from "../../appcomponents/RenderToast";
import styles from "./ResetPassword.module.css";

function PageShell({ children }) {
  return (
    <div className={styles.page}>
      <div className={styles.brandPanel}>
        <span className={styles.brandPattern} aria-hidden="true" />
        <div className={styles.brandContent}>
          <img
            src="/img/logo/mit-logo-w.webp"
            alt="MIT4Doctors"
            className={styles.brandLogo}
          />
          <h2 className={styles.brandTitle}>Welcome back</h2>
          <p className={styles.brandText}>
            Sign in to manage blogs, users, and permissions from one place.
          </p>
        </div>
      </div>
      <div className={styles.formPanel}>
        <div className={styles.card}>{children}</div>
      </div>
    </div>
  );
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [show, setShow] = useState({ password: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || done) return;

    if (!form.password || !form.confirm) {
      RenderToast({ type: "error", message: "Both password fields are required." });
      return;
    }
    if (form.password.length < 6) {
      RenderToast({ type: "error", message: "Password must be at least 6 characters." });
      return;
    }
    if (form.password !== form.confirm) {
      RenderToast({ type: "error", message: "New password and confirm password must match." });
      return;
    }

    setSaving(true);
    try {
      const response = await api.post("/api/auth/reset-password.php", {
        token,
        password: form.password,
        password_confirmation: form.confirm,
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || "Unable to reset password.");
      }

      RenderToast({ type: "success", message: "Password updated successfully." });
      setDone(true);

      const isSuper = Boolean(response.data?.data?.is_super);
      const loginPath = isSuper ? "/auth/admin/login/" : "/auth/user/login/";
      setTimeout(() => navigate(loginPath, { replace: true }), 2000);
    } catch (err) {
      RenderToast({ type: "error", message: apiError(err) });
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <PageShell>
        <div className={styles.header}>
          <h1 className={styles.title}>Invalid Reset Link</h1>
          <p className={styles.subtitle}>
            This password reset link is missing or invalid. Please request a new one from the login page.
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className={styles.header}>
        <h1 className={styles.title}>Reset Password</h1>
        <p className={styles.subtitle}>Choose a new password for your account.</p>
      </div>

      {done ? (
          <div className={styles.successState}>
            <FiCheckCircle className={styles.successIcon} />
            <p>Your password has been updated. Redirecting you to login&hellip;</p>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <label className={styles.field}>
              <span className={styles.label}>New Password</span>
              <div className={styles.inputWrap}>
                <FiLock className={styles.inputIcon} />
                <input
                  type={show.password ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  className={styles.input}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShow((prev) => ({ ...prev, password: !prev.password }))}
                  aria-label={show.password ? "Hide password" : "Show password"}
                >
                  {show.password ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Confirm Password</span>
              <div className={styles.inputWrap}>
                <FiLock className={styles.inputIcon} />
                <input
                  type={show.confirm ? "text" : "password"}
                  name="confirm"
                  value={form.confirm}
                  onChange={handleChange}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  className={styles.input}
                />
                <button
                  type="button"
                  className={styles.eyeBtn}
                  onClick={() => setShow((prev) => ({ ...prev, confirm: !prev.confirm }))}
                  aria-label={show.confirm ? "Hide password" : "Show password"}
                >
                  {show.confirm ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </label>

            <button type="submit" className={styles.submit} disabled={saving}>
              {saving ? <span className={styles.spinner} /> : "Update Password"}
            </button>
          </form>
        )}
    </PageShell>
  );
}
